import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { MessageCircle } from 'lucide-react';
import './KitSwap.css';

/**
 * Club Admin → Moderation: "View conversation" on a reported Kit Swap chat
 * message (v39). A Kit Swap chat is private to the lister and the claimer —
 * the only way an admin can read one is through a report, and only the
 * reported conversation (GET /kit/admin/reports/:reportId/thread).
 */
export default function KitReportThread({ clubSlug, reportId }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['kit-report-thread', clubSlug, reportId],
    queryFn: () => axios.get(`/clubs/${clubSlug}/kit/admin/reports/${reportId}/thread`).then(r => r.data),
    enabled: open,
    retry: false,
  });

  if (!open) {
    return (
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(true)} style={{ marginBottom: 10 }}>
        <MessageCircle size={14} /> View conversation
      </button>
    );
  }
  if (isLoading) return <p className="kit-hint">Loading the conversation…</p>;
  if (isError) return <p className="kit-hint">{error?.response?.data?.error || 'Could not load the conversation.'}</p>;

  return (
    <div className="kit-chat" style={{ marginTop: 0, borderTop: 'none', paddingTop: 0, marginBottom: 10 }}>
      <p className="kit-hint" style={{ display: 'block', marginBottom: 6 }}>
        Private Kit Swap chat about <strong>{data.listing.name}</strong> between {data.lister_name} (listed it)
        and {data.claimer_name} (claimed it). You can see it because a message in it was reported.
      </p>
      <div className="kit-chat-messages" style={{ maxHeight: 320 }}>
        {data.messages.length === 0 && <div className="kit-chat-empty">No messages.</div>}
        {data.total > data.messages.length && (
          <div className="kit-chat-empty" style={{ fontSize: 12 }}>
            {data.total - data.messages.length} earlier messages not shown.
          </div>
        )}
        {data.messages.map(m => (
          <div key={m.id} className={`kit-chat-msg ${m.sender_role === 'lister' ? 'mine' : ''}`}
            style={m.id === data.reported_message_id ? { borderColor: 'var(--gold)', borderWidth: 2 } : undefined}>
            <div className="kit-chat-msg-header">
              <span className="kit-chat-msg-name">{m.sender_name}</span>
              <span className="kit-chat-msg-time">{format(parseISO(m.created_at), 'HH:mm · d MMM')}</span>
              {m.id === data.reported_message_id && <span className="kit-chat-reported">Reported</span>}
            </div>
            <div className={`kit-chat-msg-body ${m.removed ? 'kit-chat-removed' : ''}`}>
              {m.body}{m.removed ? ' (removed)' : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
